import { SalaryGridMeasure } from "@prisma/client";
import { mapSeries } from "bluebird";
import { convertCurrency } from "~/lib/money";
import { type CompensationReviewCampaignContext } from "~/services/compensation-review/compensationReviewContext";
import { computeCompaRatio } from "~/services/salary-bands/benchmark/compaRatio";
import { computeRangePenetration } from "~/services/salary-bands/benchmark/rangePenetration";

export const refreshConvertedAmounts = async (ctx: CompensationReviewCampaignContext) => {
  const employees = await ctx.prisma.compensationReviewEmployee.findMany({
    where: { campaignId: ctx.campaign.id },
    select: {
      id: true,
      baseSalary: true,
      variablePay: true,
      onTargetEarnings: true,
      budget: true,
      baseSalaryRecommendation: true,
      variablePayRecommendation: true,
      onTargetEarningsRecommendation: true,
      baseSalaryIncrease: true,
      variablePayIncrease: true,
      onTargetEarningsIncrease: true,
      currency: { select: { euroExchangeRate: true } },
      externalEmployee: {
        select: {
          id: true,
          liveSalaryRangeEmployee: {
            select: {
              range: {
                select: {
                  midpoint: true,
                  min: true,
                  max: true,
                  band: {
                    select: {
                      currency: true,
                      measure: true,
                    },
                  },
                },
              },
              orderingCompaRatio: true,
              orderingRangePenetration: true,
            },
          },
        },
      },
      recommendations: {
        select: {
          id: true,
          baseSalaryRecommendation: true,
          variablePayRecommendation: true,
          onTargetEarningsRecommendation: true,
          baseSalaryIncrease: true,
          variablePayIncrease: true,
        },
      },
    },
  });

  await mapSeries(employees, async (employee) => {
    const convertAmount = <Amount extends number | null, ReturnType = Amount extends number ? number : null>(
      amount: Amount
    ): ReturnType => {
      if (amount === null) return null as ReturnType;

      return convertCurrency(amount, employee.currency, ctx.campaign.currency) as ReturnType;
    };

    const salary =
      employee.externalEmployee.liveSalaryRangeEmployee?.range.band.measure === SalaryGridMeasure.BASE_SALARY
        ? employee.baseSalary
        : employee.onTargetEarnings;

    const payload = {
      convertedBaseSalary: convertAmount(employee.baseSalary),
      convertedVariablePay: convertAmount(employee.variablePay),
      convertedOnTargetEarnings: convertAmount(employee.onTargetEarnings),
      convertedBudget: convertAmount(employee.budget),
      convertedBaseSalaryRecommendation: convertAmount(employee.baseSalaryRecommendation),
      convertedVariablePayRecommendation: convertAmount(employee.variablePayRecommendation),
      convertedOnTargetEarningsRecommendation: convertAmount(employee.onTargetEarningsRecommendation),
      convertedBaseSalaryIncrease: convertAmount(employee.baseSalaryIncrease),
      convertedVariablePayIncrease: convertAmount(employee.variablePayIncrease),
      convertedOnTargetEarningsIncrease: convertAmount(employee.onTargetEarningsIncrease),
      convertedNewOnTargetEarnings: convertAmount(
        employee.onTargetEarningsIncrease ? employee.onTargetEarnings + employee.onTargetEarningsIncrease : null
      ),
      compaRatio: employee.externalEmployee.liveSalaryRangeEmployee?.range.midpoint
        ? computeCompaRatio({
            amount: convertAmount(salary),
            midpoint: convertCurrency(
              employee.externalEmployee.liveSalaryRangeEmployee.range.midpoint,
              employee.externalEmployee.liveSalaryRangeEmployee.range.band.currency,
              ctx.campaign.currency
            ),
          })
        : null,
      rangePenetration:
        employee.externalEmployee.liveSalaryRangeEmployee?.range.min &&
        employee.externalEmployee.liveSalaryRangeEmployee?.range.max
          ? computeRangePenetration({
              amount: convertAmount(salary),
              min: convertCurrency(
                employee.externalEmployee.liveSalaryRangeEmployee.range.min,
                employee.externalEmployee.liveSalaryRangeEmployee.range.band.currency,
                ctx.campaign.currency
              ),
              max: convertCurrency(
                employee.externalEmployee.liveSalaryRangeEmployee.range.max,
                employee.externalEmployee.liveSalaryRangeEmployee.range.band.currency,
                ctx.campaign.currency
              ),
            })
          : null,
    };

    await ctx.prisma.compensationReviewEmployee.update({
      where: { id: employee.id },
      data: payload,
    });

    await mapSeries(employee.recommendations, async (recommendation) => {
      const payload = {
        convertedBaseSalaryRecommendation: convertAmount(recommendation.baseSalaryRecommendation),
        convertedVariablePayRecommendation: convertAmount(recommendation.variablePayRecommendation),
        convertedOnTargetEarningsRecommendation: convertAmount(recommendation.onTargetEarningsRecommendation),
        convertedOnTargetEarningsIncrease: convertAmount(
          employee.baseSalaryIncrease ? employee.baseSalaryIncrease + (employee.variablePayIncrease ?? 0) : null
        ),
      };

      await ctx.prisma.compensationReviewRecommendation.update({
        where: { id: recommendation.id },
        data: payload,
      });
    });
  });
};
